import cssObjectForLink from "./functions/cssObjectForLink";
import merge from "lodash.merge";
import styled from "styled-components";
import { getFontAndLineHeightArray } from "../../../typography/Typography/Typography.styles";
import { StyledLinkProps } from "../Link.types";
import { resolvePaletteRoleForegroundToken } from "../../../../functions/resolvePaletteRoleForegroundToken";

const EASE_IN_DURATION = 300;
const EASE_OUT_DURATION = 500;
const EASE_IN = "ease-in";
const EASE_OUT = "ease-out";
const SNAP = "snap";

const StyledLink = styled.a<StyledLinkProps>`
	display: ${({ inline }) => (inline ? "inline-flex" : "flex")};
	gap: 4px;
	cursor: pointer;

	${({ disabled }) =>
		disabled === true ? `pointer-events: none;` : `pointer-events: auto;`};

	${({ theme, size, viewport }) => {
		const fontAndLineHeight = getFontAndLineHeightArray(size, viewport);
		return {
			fontSize: fontAndLineHeight[0],
			lineHeight: fontAndLineHeight[1],
			fontWeight: theme.fontWeights.values.heaviest,
		};
	}}

	transition-property: color, text-decoration-color, fill;
	transition-timing-function: ${EASE_IN};
	transition-duration: ${EASE_IN_DURATION}ms;
	text-decoration: ${({ inline }) => (inline ? "underline" : "none")};

	&:hover {
		transition-timing-function: ${EASE_OUT};
		transition-duration: ${EASE_OUT_DURATION}ms;
		text-decoration: underline;
	}
	&:active {
		transition-timing-function: ${EASE_IN};
		transition-duration: ${EASE_IN_DURATION}ms;
		text-decoration: underline;
	}
	&:focus {
		transition-timing-function: ${SNAP};
		transition-duration: 0ms;
		text-decoration: underline;
	}

	${(props) => {
		const palette = props.theme.palette;
		const COLOR_CTA_DEFAULT = palette.hexForRole("cta_default");
		const COLOR_CTA_HOVER = palette.hexForRole("cta_hover");
		const COLOR_CTA_ACTIVE = palette.hexForRole("cta_active");
		const COLOR_CTA_FOCUS = palette.hexForRole("cta_focus");
		const COLOR_CTA_DISABLED = palette.hexForRole("cta_disabled");
		const COLOR_NEUTRAL_700 = palette.hexForMandatory("neutral_700");
		const COLOR_PRIMARY_ACCENT_300 =
			palette.hexForMandatory("primary_accent_300");
		const FG_COLOR_1 = palette.hexForRole(
			resolvePaletteRoleForegroundToken(props.colorOn)
		);
		const BROWSER_VISITED = "#681DA8";

		switch (props.colorOn) {
			case "bg_dark":
				return merge(
					cssObjectForLink(FG_COLOR_1, props.disabled, COLOR_NEUTRAL_700),
					{
						"&:hover": cssObjectForLink(
							COLOR_CTA_HOVER,
							props.disabled,
							COLOR_NEUTRAL_700
						),
						"&:active": cssObjectForLink(
							COLOR_CTA_ACTIVE,
							props.disabled,
							COLOR_NEUTRAL_700
						),
						"&:focus": cssObjectForLink(
							COLOR_PRIMARY_ACCENT_300,
							props.disabled,
							COLOR_NEUTRAL_700
						),
						"&:visited": props.inline
							? cssObjectForLink(
									BROWSER_VISITED,
									props.disabled,
									COLOR_NEUTRAL_700
							  )
							: undefined,
					}
				);
			case "bg_dark_variant":
				return merge(
					cssObjectForLink(FG_COLOR_1, props.disabled, COLOR_NEUTRAL_700),
					{
						"&:hover": cssObjectForLink(
							COLOR_CTA_HOVER,
							props.disabled,
							COLOR_NEUTRAL_700
						),
						"&:active": cssObjectForLink(
							COLOR_CTA_ACTIVE,
							props.disabled,
							COLOR_NEUTRAL_700
						),
						"&:focus": cssObjectForLink(
							COLOR_PRIMARY_ACCENT_300,
							props.disabled,
							COLOR_NEUTRAL_700
						),
						"&:visited": props.inline
							? cssObjectForLink(
									BROWSER_VISITED,
									props.disabled,
									COLOR_NEUTRAL_700
							  )
							: undefined,
					}
				);
			default: {
				return merge(
					cssObjectForLink(
						COLOR_CTA_DEFAULT,
						props.disabled,
						COLOR_CTA_DISABLED
					),
					{
						"&:hover": cssObjectForLink(
							COLOR_CTA_HOVER,
							props.disabled,
							COLOR_CTA_DISABLED
						),
						"&:active": cssObjectForLink(
							COLOR_CTA_ACTIVE,
							props.disabled,
							COLOR_CTA_DISABLED
						),
						"&:focus": cssObjectForLink(
							COLOR_CTA_FOCUS,
							props.disabled,
							COLOR_CTA_DISABLED
						),
						"&:visited": props.inline
							? cssObjectForLink(
									BROWSER_VISITED,
									props.disabled,
									COLOR_CTA_DISABLED
							  )
							: undefined,
					}
				);
			}
		}
	}};

	// Brand customizations
	${(props) => {
		if (props.inline && !props.disabled) {
			const linkVisitedState = props.theme.components.link.visited;
			return linkVisitedState ? linkVisitedState : undefined;
		}
		return undefined;
	}}
`;

export default StyledLink;
